import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { applyInboxSorting, listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { Order, SortParams } from "@/types/types";

import { DEFAULT_SORT_BY, DEFAULT_SORT_KEY, DEFAULT_SORT_ORDER } from "./sort-by-dropdown.types";

const SORT_BY_PARAM = "sortBy";
const SORT_ORDER_PARAM = "sortOrder";

export interface SortUrlSync {
	/** The raw selection for the UI's active state — `""` when nothing is selected. */
	sortBy: string;
	sortOrder: Order;
	onSort: (params: SortParams) => void;
}

/**
 * Makes the **URL** the source of truth for the lead sort (PREC-3544): the sort is read from the
 * `sortBy`/`sortOrder` query params and mirrored into the shared `sortingInbox` state (which still
 * drives the data-loads). Because each surface lives on its own route, the sort therefore:
 *  - **persists across reloads / is shareable** (it's in the URL),
 *  - **does not leak across surfaces** — navigating e.g. List → Review-to-send lands on a route with
 *    no sort params, so it resets, and
 *  - **loads with nothing selected** — a fresh page has no sort params, so no option is highlighted.
 *
 * Two non-column states both resolve to the recency default for the data-load (which the
 * smart-ordered surfaces treat as their schedule/paused/replies-aware order): the no-selection state
 * (no params) and the explicitly-picked **Default** option ({@link DEFAULT_SORT_KEY} sentinel). Only
 * the latter highlights the Default option; the backend never sees the sentinel.
 *
 * `onSort` writes the chosen sort to the URL (replacing history, merging with existing params); an
 * empty `sortBy` clears the params (back to no-selection).
 */
export const useSortUrlSync = (): SortUrlSync => {
	const [searchParams, setSearchParams] = useSearchParams();
	const dispatch = useAppDispatch();
	const { sortingInbox } = useAppSelector(listSelector);

	const rawSortBy = searchParams.get(SORT_BY_PARAM);
	const sortOrder = (searchParams.get(SORT_ORDER_PARAM) as Order) || DEFAULT_SORT_ORDER;

	// A real column sort is anything explicit other than the Default sentinel.
	const isColumnSort = !!rawSortBy && rawSortBy !== DEFAULT_SORT_KEY;
	const effectiveSortBy = isColumnSort ? rawSortBy : DEFAULT_SORT_BY;
	const effectiveSortOrder = isColumnSort ? sortOrder : DEFAULT_SORT_ORDER;

	// Mirror the URL sort into state for the data-loads — but ONLY when it actually differs, so a
	// fresh mount with the default already in place doesn't re-assign `sortingInbox` (a new reference
	// would abort the surfaces' in-flight initial fetch — same args, so it never re-runs — leaving the
	// list empty until a sort is picked).
	useEffect(() => {
		if (sortingInbox.sortBy !== effectiveSortBy || sortingInbox.sortOrder !== effectiveSortOrder) {
			dispatch(applyInboxSorting({ sortBy: effectiveSortBy, sortOrder: effectiveSortOrder }));
		}
	}, [dispatch, sortingInbox, effectiveSortBy, effectiveSortOrder]);

	// Reset to the default when LEAVING a surface, so the next one mounts already-clean and never gets
	// a disruptive post-mount re-sort (those abort-based fetch effects cancel their initial request
	// when `sortingInbox` changes right after mount). The URL still holds the sort, so returning to a
	// surface re-applies it. Runs on unmount only — `MessagesFilterBar`/UnassignedTable stay mounted
	// within a surface, so this never resets a sort mid-surface.
	useEffect(
		() => () => {
			dispatch(applyInboxSorting({ sortBy: DEFAULT_SORT_BY, sortOrder: DEFAULT_SORT_ORDER }));
		},
		[dispatch]
	);

	const onSort = (params: SortParams) => {
		setSearchParams(
			(prev) => {
				if (params.sortBy === DEFAULT_SORT_KEY) {
					prev.set(SORT_BY_PARAM, DEFAULT_SORT_KEY);
					prev.delete(SORT_ORDER_PARAM);
				} else if (!params.sortBy) {
					prev.delete(SORT_BY_PARAM);
					prev.delete(SORT_ORDER_PARAM);
				} else {
					prev.set(SORT_BY_PARAM, params.sortBy);
					prev.set(SORT_ORDER_PARAM, params.sortOrder);
				}

				return prev;
			},
			{ replace: true }
		);
	};

	return { sortBy: rawSortBy ?? "", sortOrder, onSort };
};
